import type {AppendMessage} from '@assistant-ui/core';

import type {
  HttpUserPrompt,
  PagePromptContent as WirePageContent,
  PromptPart,
} from '../client/promptParts';
import {auiContentToStoreParts} from './messageConversions';
import {buildPromptContextParts, type PromptContextParams} from './pageContext';
import {extractTurnId} from './runtime/turn';
import {
  type ContentPart,
  type PagePromptContent,
  type Thread,
} from './store/thread';

/**
 * BuiltPrompt is the frontend representation of a user prompt produced by the
 * composer (or reconstructed from store content for re-runs). It pairs the
 * backend shape (`httpParts`) with the store shape (`storeParts`) so callers
 * can both POST to `/turns` and optimistically render in the message tree
 * without re-deriving either from the other.
 */
export interface BuiltPrompt {
  httpParts: HttpUserPrompt;
  storeParts: ContentPart[];
  plainText: string;
}

/**
 * buildAssembledPrompt produces the full wire prompt a turn would POST for the
 * given user parts and route: the same context parts + user parts the composer
 * sends, converted to their backend shape. For preview/debugging only.
 */
export function buildAssembledPrompt(
  userStoreParts: ContentPart[],
  params: PromptContextParams
): HttpUserPrompt {
  return withPromptContext(builtPromptFromStoreContent(userStoreParts), params)
    .httpParts;
}

/**
 * withPromptContext prepends nav/page context parts (URL, entity/project/run
 * scope, page-content slices) to the user's prompt. Applied once per user
 * message at the runtime boundary so every entry point (composer, suggestions,
 * any future quick-action) carries the same context.
 */
export function withPromptContext(
  userBuilt: BuiltPrompt,
  params: PromptContextParams
): BuiltPrompt {
  const contextParts = buildPromptContextParts(params);
  return builtPromptFromStoreContent([
    ...contextParts,
    ...userBuilt.storeParts,
  ]);
}

/**
 * readBuiltPrompt derives a {@link BuiltPrompt} from an AUI `AppendMessage`.
 * AUI `content` is the sole source of truth: the composer emits structured
 * parts (text + `data` parts for wandb mentions)
 */
export function readBuiltPrompt(message: AppendMessage): BuiltPrompt | null {
  const storeParts = auiContentToStoreParts(
    message.content as ReadonlyArray<{type: string; [k: string]: unknown}>
  );
  if (storeParts.length === 0) {
    return null;
  }
  return builtPromptFromStoreContent(storeParts);
}

/**
 * resolveFallbackParentTurnId supplies a parent-turn fallback for the runtime
 * when `parentId` doesn't yield a turn-encoded ancestor (e.g. the previous turn
 * was cancelled before any extractable nodes were created). `undefined` once
 * past the thread's first turn.
 */
export function resolveFallbackParentTurnId(
  thread: Thread | undefined,
  parentId: string | null
): string | undefined {
  if (!thread?.latestTurnId || parentId == null) {
    return undefined;
  }

  return extractTurnId(parentId) == null ? thread.latestTurnId : undefined;
}

/**
 * resolveTurnParent derives the `parentId`/`parentTurnId` pair a new turn on
 * `thread` should send, from the thread's active branch head. Shared by `onNew`
 * and the queue drainer so both send a turn the same way.
 */
export function resolveTurnParent(thread: Thread | undefined): {
  parentId: string | null;
  parentTurnId: string | undefined;
} {
  const parentId = thread?.messageTree.currentNodeId ?? null;
  return {
    parentId,
    parentTurnId: resolveFallbackParentTurnId(thread, parentId),
  };
}

/** extractText extracts plain text from an AUI `AppendMessage`'s content parts. */
export function extractText(message: AppendMessage): string {
  return (message.content as readonly {type: string; text?: string}[])
    .filter(p => p.type === 'text')
    .map(p => p.text ?? '')
    .join('\n\n');
}

/** builtPromptFromText builds a {@link BuiltPrompt} from plain text. */
export function builtPromptFromText(text: string): BuiltPrompt {
  return {
    httpParts: text,
    storeParts: [{type: 'text', text, status: 'complete'}],
    plainText: text,
  };
}

/**
 * builtPromptFromStoreContent builds a {@link BuiltPrompt} from store content
 * parts, deriving the backend shape and a plain-text rendering.
 */
export function builtPromptFromStoreContent(parts: ContentPart[]): BuiltPrompt {
  const httpParts: PromptPart[] = [];
  const plainTextChunks: string[] = [];

  for (const p of parts) {
    switch (p.type) {
      case 'text':
        httpParts.push({type: 'text', text: p.text});
        plainTextChunks.push(p.text);
        break;
      case 'image':
        httpParts.push({
          type: 'image',
          media_type: p.mediaType,
          base64_data: p.base64Data,
        });
        break;
      case 'wandb-entity':
        httpParts.push({type: 'wandb_entity', entity_name: p.entityName});
        break;
      case 'wandb-project':
        httpParts.push({
          type: 'wandb_project',
          entity_name: p.entityName,
          project_name: p.projectName,
        });
        break;
      case 'wandb-run':
        httpParts.push({
          type: 'wandb_run',
          entity_name: p.entityName,
          project_name: p.projectName,
          run_name: p.runName,
        });
        plainTextChunks.push(`@${p.runDisplayName ?? p.runName}`);
        break;
      case 'wandb-call':
        httpParts.push({
          type: 'wandb_call',
          entity_name: p.entityName,
          project_name: p.projectName,
          call_id: p.callId,
          display_name: p.callDisplayName,
        });
        break;
      case 'wandb-conversation':
        httpParts.push({
          type: 'wandb_conversation',
          entity_name: p.entityName,
          project_name: p.projectName,
          conversation_id: p.conversationId,
        });
        break;
      case 'wandb-artifact-collection':
        httpParts.push({
          type: 'wandb_artifact_collection',
          collection_id: p.collectionId,
        });
        break;
      case 'wandb-workspace-panel':
        httpParts.push({
          type: 'wandb_workspace_panel',
          entity_name: p.entityName,
          project_name: p.projectName,
          view_id: p.viewId,
          view_type: p.viewType,
          panel_id: p.panelId,
          panel_type: p.panelType,
          title: p.title,
          query_panel_table_key: p.queryPanelTableKey,
          query_panel_table_key_type: p.queryPanelTableKeyType,
        });
        break;
      case 'wandb-web-page-state':
        httpParts.push({
          type: 'wandb_web_page_state',
          url: p.url,
          product_id: p.productId,
          app_path: p.appPath,
          entity_name: p.entityName,
          project_name: p.projectName,
          content: p.content?.map(toWirePageContent),
          query_params: p.queryParams,
        });
        break;
      case 'unknown':
        // Unknown parts are dropped on retransmission.
        // Silently dropping now, but can add logs if needed.
        break;
    }
  }
  return {
    httpParts: httpParts.length > 0 ? httpParts : '',
    storeParts: parts,
    plainText: plainTextChunks.join('').trim(),
  };
}

// toWirePageContent converts a store page-content variant to its snake_case
// wire shape. The return type drives exhaustiveness over the variant union.
function toWirePageContent(content: PagePromptContent): WirePageContent {
  switch (content.type) {
    case 'workspace':
      return {
        type: 'workspace',
        view_id: content.viewId,
        view_name: content.viewName,
        view_type: content.viewType,
        view_scope: content.viewScope,
        run_count_total: content.runCountTotal,
        run_count_filtered: content.runCountFiltered,
        run_count_listed: content.runCountListed,
        visible_run_names_preview: content.visibleRunNamesPreview,
        pinned_run_names_preview: content.pinnedRunNamesPreview,
        num_runs_visible: content.numRunsVisible,
        num_runs_hidden: content.numRunsHidden,
        list_only_visible_runs: content.listOnlyVisibleRuns,
        run_filters_expression: content.runFiltersExpression,
        panel_search_query: content.panelSearchQuery,
        run_selector_grouping: content.runSelectorGrouping,
      };
    case 'report':
      return {
        type: 'report',
        title: content.title,
        description: content.description,
        run_sets: content.runSets?.map(runSet => ({
          name: runSet.name,
          entity_name: runSet.entityName,
          project_name: runSet.projectName,
          run_count_filtered: runSet.runCountFiltered,
          run_filters_expression: runSet.runFiltersExpression,
          enabled: runSet.enabled,
        })),
      };
    case 'sweep':
      return {
        type: 'sweep',
        state: content.state,
        method: content.method,
        metric: content.metric,
        run_count: content.runCount,
        run_count_expected: content.runCountExpected,
      };
  }
}
